import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  Badge,
  Button,
  Divider,
  useToast,
  SimpleGrid,
  Input,
  InputGroup,
  InputLeftElement,
  Stack,
  IconButton,
  Flex,
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";
import { RefreshCcw } from "lucide-react";
import BookingStatusTimeline from "./CarBookingStatusTimeline";
import BookingDetailsDrawer from "./BookingDetailsDrawer";
import { useAuth } from "../../../auth/AuthContext";
import { useCustomTheme } from "../../../contexts/CustomThemeContext";
import GlobalSpinner from "../../CustomReusables/GlobalSpinner";

const cardsBorderRadii ="8px";

const CARS_API =
  (process.env.REACT_APP_BACKEND_URL || "") + "/api/v1/travel/cars";

function money(n: number) {
  return `MWK ${Number(n || 0).toLocaleString()}`;
}

function statusColor(status: string) {
  const map: Record<string, string> = {
    pending: "gray",
    awaiting_owner_approval: "yellow",
    approved: "green",
    declined: "red",
    paid: "blue",
    active: "purple",
    completed: "green",
    cancelled: "orange",
    refunded: "teal",
  };
  return map[status] || "gray";
}

export default function MyHireBookings() {
  const toast = useToast();
  const user = useAuth();
  const { theme } = useCustomTheme();
  const userId = user.user?.uid || "";

  const [loading, setLoading] = useState(false);
  const [bookings, setBookings] = useState<any[]>([]);
  const [query, setQuery] = useState("");

  const [selected, setSelected] = useState<any | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return bookings;
    return bookings.filter((b) => {
      const id = String(b._id || "").toLowerCase();
      const status = String(b.status || "").toLowerCase();
      const carId = String(b.carId || "").toLowerCase();
      return id.includes(q) || status.includes(q) || carId.includes(q);
    });
  }, [bookings, query]);

  const stats = useMemo(() => {
    const total = bookings.length;
    const active = bookings.filter((b) =>
      ["paid", "active", "approved"].includes(b.status),
    ).length;
    const completed = bookings.filter((b) => b.status === "completed").length;
    const cancelled = bookings.filter((b) =>
      ["cancelled", "refunded", "declined"].includes(b.status),
    ).length;
    return { total, active, completed, cancelled };
  }, [bookings]);

  const load = async () => {
    if (!userId) {
      toast({
        title: "User ID missing (localStorage userId)",
        status: "warning",
      });
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get(`${CARS_API}/hire/user/bookings`, {
        params: { userId },
      });
      if (res.data?.ok) setBookings(res.data.bookings || []);
    } catch (err: any) {
      toast({
        title: "Failed to load bookings",
        description: err?.response?.data?.error || err?.message,
        status: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    console.log("use", user);
    // eslint-disable-next-line
  }, []);

  const cancel = async (bookingId: string) => {
    try {
      const res = await axios.post(
        `${CARS_API}/hire/bookings/${bookingId}/cancel`,
        { userId },
      );
      if (!res.data?.ok) throw new Error(res.data?.error || "Failed");

      toast({
        title: "Booking cancelled",
        description: `Refund: ${res.data.refundPercent}% • Amount: MWK ${Number(
          res.data.refundable || 0,
        ).toLocaleString()}`,
        status: "success",
      });

      if (selected?._id === bookingId) {
        setDrawerOpen(false);
      }

      await load();
    } catch (err: any) {
      toast({
        title: "Cancel failed",
        description: err?.response?.data?.error || err?.message,
        status: "error",
      });
    }
  };

  const openDetails = (b: any) => {
    setSelected(b);
    setDrawerOpen(true);
  };

  return (
    <Box
      p={{ base: 4, md: 8 }}
      bg={theme.surfaceAlt}
      minH="100%"
      color={theme.text}
    >
      <Stack
        direction={{ base: "column", md: "row" }}
        justify="space-between"
        align={{ md: "center" }}
        spacing={4}
      >
        <Box>
          <Heading size="lg" color={theme.text}>
            My Hire Bookings
          </Heading>
          <Text color={theme.textMuted} mt={1}>
            Track progress, view details, and manage your bookings.
          </Text>
        </Box>

        <IconButton
          aria-label="Refresh"
          icon={<RefreshCcw size={18} />}
          onClick={load}
          variant="outline"
          bg={theme.surface}
          borderColor={theme.borderStrong}
          color={theme.text}
          _hover={{ bg: theme.hover }}
        />
      </Stack>

      {/* Summary cards */}
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mt={6}>
        <Box
          p={4}
          borderRadius={cardsBorderRadii}
          bg={theme.surface}
          border="1px solid"
          borderColor={theme.border}
          boxShadow={theme.shadowSm}
        >
          <Text fontSize="xs" color={theme.textSubtle}>
            Total Bookings
          </Text>
          <Text fontSize="2xl" fontWeight="bold" color={theme.text}>
            {stats.total}
          </Text>
        </Box>
        <Box
          p={4}
          borderRadius={cardsBorderRadii}
          bg={theme.surface}
          border="1px solid"
          borderColor={theme.border}
          boxShadow={theme.shadowSm}
        >
          <Text fontSize="xs" color={theme.textSubtle}>
            Active / In Progress
          </Text>
          <Text fontSize="2xl" fontWeight="bold" color="purple.600">
            {stats.active}
          </Text>
        </Box>
        <Box
          p={4}
          borderRadius={cardsBorderRadii}
          bg={theme.surface}
          border="1px solid"
          borderColor={theme.border}
          boxShadow={theme.shadowSm}
        >
          <Text fontSize="xs" color={theme.textSubtle}>
            Completed
          </Text>
          <Text fontSize="2xl" fontWeight="bold" color="green.600">
            {stats.completed}
          </Text>
        </Box>
        <Box
          p={4}
          borderRadius={cardsBorderRadii}
          bg={theme.surface}
          border="1px solid"
          borderColor={theme.border}
          boxShadow={theme.shadowSm}
        >
          <Text fontSize="xs" color={theme.textSubtle}>
            Cancelled / Closed
          </Text>
          <Text fontSize="2xl" fontWeight="bold" color="orange.600">
            {stats.cancelled}
          </Text>
        </Box>
      </SimpleGrid>

      <HStack mt={6} spacing={4} align="stretch" flexWrap="wrap">
        <InputGroup maxW={{ base: "100%", md: "420px" }}>
          <InputLeftElement pointerEvents="none">
            <SearchIcon color={theme.textSubtle} />
          </InputLeftElement>
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by booking ID, status, carId…"
            bg={theme.inputBg}
            color={theme.text}
            borderColor={theme.inputBorder}
            _placeholder={{ color: theme.inputPlaceholder }}
            _hover={{ borderColor: theme.inputBorderFocus }}
            _focus={{
              borderColor: theme.inputBorderFocus,
              boxShadow: `0 0 0 1px ${theme.inputBorderFocus}`,
            }}
          />
        </InputGroup>

        {loading && (
          <HStack
            bg={theme.surface}
            px={3}
            borderRadius={cardsBorderRadii}
            border="1px solid"
            borderColor={theme.border}
          >
            <GlobalSpinner />
            <Text color={theme.textMuted} fontSize="sm">
              Loading…
            </Text>
          </HStack>
        )}
      </HStack>

      <Divider my={6} borderColor={theme.divider} />

      <VStack align="stretch" spacing={4}>
        {filtered.length === 0 ? (
          <Box
            p={8}
            border="1px dashed"
            borderColor={theme.borderStrong}
            borderRadius={cardsBorderRadii}
            textAlign="center"
            bg={theme.surface}
          >
            <Text fontWeight="semibold" color={theme.text}>
              No bookings found
            </Text>
            <Text color={theme.textMuted} fontSize="sm" mt={1}>
              Once you book a car, it will appear here.
            </Text>
          </Box>
        ) : (
          filtered.map((b) => {
            const totalDue = Number(b?.price?.totalDue || 0);
            const depositHold = Number(b?.price?.depositHold || 0);

            const canCancel = ![
              "completed",
              "cancelled",
              "refunded",
              "declined",
            ].includes(b.status);

            return (
              <Box
                key={b._id}
                p={4}
                border="1px solid"
                borderColor={theme.border}
                borderRadius={cardsBorderRadii}
                bg={theme.surface}
                boxShadow={theme.shadowSm}
                _hover={{
                  boxShadow: theme.shadowMd,
                  borderColor: theme.borderStrong,
                }}
                transition="all 0.2s ease"
              >
                <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={4}>
                  <Box>
                    <HStack spacing={3} flexWrap="wrap">
                      <Text fontWeight="bold" fontSize="md" color={theme.text}>
                        Booking #{String(b._id).slice(-6)}
                      </Text>
                      <Badge
                        colorScheme={statusColor(b.status)}
                        px={2.5}
                        py={1}
                        borderRadius={cardsBorderRadii}
                        textTransform="none"
                      >
                        {String(b.status || "").replaceAll("_", " ")}
                      </Badge>
                    </HStack>

                    <Text fontSize="sm" color={theme.textMuted} mt={2}>
                      {new Date(b.pickupAt).toLocaleString()} →{" "}
                      {new Date(b.returnAt).toLocaleString()}
                    </Text>

                    <Text fontSize="sm" color={theme.textMuted} mt={1}>
                      Insurance: {b.insurancePlan || "none"} • Delivery:{" "}
                      {b.deliveryRequested ? "Yes" : "No"}
                    </Text>

                    <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={3} mt={3}>
                      <Box
                        p={3}
                        bg={theme.surfaceAlt}
                        borderRadius={cardsBorderRadii}
                        border="1px solid"
                        borderColor={theme.border}
                      >
                        <Text fontSize="xs" color={theme.textSubtle}>
                          Total Due
                        </Text>
                        <Text fontWeight="bold" color={theme.text}>
                          {money(totalDue)}
                        </Text>
                      </Box>
                    </SimpleGrid>
                  </Box>

                  <Box>
                    <BookingStatusTimeline status={b.status} compact />

                    <Flex
                      mt={4}
                      gap={3}
                      justify={{ base: "flex-start", md: "flex-end" }}
                      wrap="wrap"
                    >
                      <Button
                        variant="outline"
                        onClick={() => openDetails(b)}
                        borderColor={theme.borderStrong}
                        color={theme.text}
                        _hover={{ bg: theme.hover }}
                      >
                        View Details
                      </Button>

                      <Button
                        colorScheme="orange"
                        variant={canCancel ? "solid" : "outline"}
                        isDisabled={!canCancel}
                        onClick={() => cancel(b._id)}
                      >
                        Cancel Booking
                      </Button>
                    </Flex>
                  </Box>
                </SimpleGrid>
              </Box>
            );
          })
        )}
      </VStack>

      <BookingDetailsDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        booking={selected}
        mode="customer"
        onCancel={() => selected && cancel(selected._id)}
      />
    </Box>
  );
}
